import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {PageHeader} from "../components/layout/PageHeader";
import {Button, Col, Form, Input, Pagination, Row, Select, Spin} from "antd";
import {useHistory} from "react-router-dom";
import {createTemplate, getTemplate, useTemplate, useTemplateCreate} from "../hooks/template";
import {TemplateCreateEditDto, TemplateDto, TemplateListItemDto} from "../generated-api";
import {Filter, Grid, PlusCircle} from "react-feather";
import {DEFAULT_PAGE_SIZE} from "../constants";
import {BackButton} from "../components/layout/BackButton";
import {getBusinessSectors, useBusinessSectors} from "../hooks/businessSectors";
import {filtersSubject, useFilters} from "../hooks";

const pageSize = DEFAULT_PAGE_SIZE;

export function TemplatesPage() {
    const {t} = useTranslation();
    const history = useHistory();
    useEffect(() => {
        getBusinessSectors().then().catch(console.error);
        getTemplate(false).then().catch(console.error);
    }, [true]);
    const templateResponse = useTemplate();
    const templates = templateResponse.data || [];
    const templateCreateResponse = useTemplateCreate();
    const businessSectorsResponse = useBusinessSectors();
    const businessSectors = (businessSectorsResponse || {}).data || [];
    const [page, setPage] = useState(1);
    const [addFormCollapsed, setAddFormCollapsed] = useState(true);
    const [form] = Form.useForm();
    const filters: any = useFilters();
    const [filterCollapsed, setFilterCollapsed] = useState(!filters.filterBusinessSector);
    const [filterBusinessSector, setFilterBusinessSector] = useState<any>(filters.filterBusinessSector);

    const validateMessages = {
        required: t('error.required'),
        string: {
            max: t('error.string.max')
        }
    };

    const onFinish = (values: TemplateCreateEditDto): void => {
        createTemplate(values).then((template: TemplateDto) => {
            history.push('/templates/' + template.id);
        }).catch(console.error);
    };

    const filtered = filterBusinessSector ? templates.filter((item: TemplateListItemDto) => item.businessSectorId === filterBusinessSector) : templates;
    const processed = [];
    for (let i = (page - 1) * pageSize; i < page * pageSize; i++) {
        filtered[i] && processed.push(filtered[i]);
    }

    return (
        <Spin spinning={templateResponse.loading}>
            <PageHeader/>
            <div className={'top-block accent6-background'}>
                <div className={'flex-row'}>
                    <h1 className={'h1 flex-auto'}><BackButton/><Grid/>{t('templates.title')}</h1>
                    <a href={'/companies'} className={'title-icon-button mr-1 ' + (filterCollapsed ? '' : 'toggle')}
                       onClick={event => {
                           event.preventDefault();
                           setFilterCollapsed(!filterCollapsed);
                           setAddFormCollapsed(true);
                       }}>
                        <Filter/>
                    </a>
                    <a href={'/templates/add'} className={'title-icon-button ' + (addFormCollapsed ? '' : 'toggle')}
                       onClick={event => {
                           event.preventDefault();
                           setAddFormCollapsed(!addFormCollapsed);
                           setFilterCollapsed(true);
                           form.resetFields();
                       }}>
                        <PlusCircle className={addFormCollapsed ? '' : 'rotate45'}/>
                    </a>
                </div>
                <Form className={filterCollapsed ? 'collapsed' : 'expanded'}>
                    <Row className={'row-offset'}>
                        <Col span={24} md={12} lg={8}>
                            <Form.Item className={'mb-0'}>
                                <Select placeholder={t('template.businessSector')} allowClear showSearch
                                        style={{width: '100%'}}
                                        value={filterBusinessSector}
                                        onChange={filterBusinessSector => {
                                            setFilterBusinessSector(filterBusinessSector);
                                            filtersSubject.next({filterBusinessSector});
                                        }}
                                        filterOption={(input, option): any =>
                                            String((option || {}).children || '').toLowerCase().indexOf(String(input || '').toLowerCase()) >= 0
                                        }>
                                    {businessSectors.map((item: any) =>
                                        <Select.Option key={item.id} value={String(item.id)}>{item.name}</Select.Option>
                                    )}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
                <Spin spinning={templateCreateResponse.loading}>
                    <Form form={form} name="template" onFinish={onFinish} validateMessages={validateMessages}
                          autoComplete={'off'} className={addFormCollapsed ? 'collapsed' : 'expanded'}
                          validateTrigger={'onSubmit'}>
                        <Row className={'row-offset'}>
                            <Col span={24} md={12} xl={8}>
                                <Form.Item name={'businessSectorId'}
                                           rules={[{required: true}]}>
                                    <Select allowClear showSearch style={{width: '100%'}}
                                            placeholder={t('company.businessSector')}
                                            filterOption={(input, option): any =>
                                                String((option || {}).children || '').toLowerCase().indexOf(String(input || '').toLowerCase()) >= 0
                                            }>
                                        {businessSectors.map((item: any) =>
                                            <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>
                                        )}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={24} md={12} xl={8}>
                                <Form.Item name={'name'} rules={[{required: true}, {max: 512}]}>
                                    <Input placeholder={t('template.name')}/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item className={'no-margin'}>
                            <Button type="primary" className={'white-background text-default w-100'} htmlType="submit">
                                {t('templates.add')}
                            </Button>
                        </Form.Item>
                    </Form>
                </Spin>
            </div>
            <div className={'subpage-content'}>
                {processed.map((template: TemplateListItemDto, index: number) =>
                    <div key={index} className={'card accent6-background'}>
                        <a href={'/templates/' + template.id} onClick={event => {
                            event.preventDefault();
                            history.push('/templates/' + template.id);
                        }}>
                            <div className={'card-header flex-row no-border'}>
                                {template.name}
                            </div>
                        </a>
                    </div>
                )}
                {!processed.length && <div className={'no-data'}>{t('general.noData')}</div>}
                {filtered.length > pageSize &&
                <Pagination onChange={setPage} pageSize={pageSize} total={filtered.length} current={page}
                            defaultCurrent={page}/>
                }
            </div>
        </Spin>
    )
}
